import React from "react"
import PropTypes from "prop-types"
import _ from "lodash"
import { styled } from "@mui/material/styles"
import { Button } from "@mui/material"
import SampleWaveform from "./SampleWaveform"
import {
  needsMoreInfoReasons,
  rejectionReasons,
} from "../utils/samplefocusDefaults"

import { observer, inject } from "mobx-react"

const EditSampleSmallCardContainer = styled('div')`
  width: 180px;
  min-width: 180px;
  margin-top: 20px !important;
  margin-left: 20px !important;
  outline: ${(props) => (props.isSelected ? "2px solid #3498da" : "none")};
  transform: ${(props) =>
    props.isSelected ? "scale(0.9) translateY(-10px)" : "none"};
  box-shadow: ${(props) =>
    props.isSelected
      ? "0 8px 17px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)"
      : "box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.16), 0 2px 10px 0 rgba(0, 0, 0, 0.12)"};
  transition: all 0.3s ease-in-out;
`

const EditSampleSmallWaveformContainer = styled('div')`
  height: 160px;
  position: relative;
  cursor: pointer;
`

const EditSampleSmallInfoContainer = styled('div')`
  padding: 0 10px 10px 10px;

  &:empty {
    padding: 0;
  }
`

const AttributesContainer = styled('ul')`
  list-style-type: none;
  list-style: none;
  margin: 10px 0 0 10px;
  padding: 0;
  font-size: 11px;
`

const Attribute = styled('li')`
  display: inline-block;
  padding-right: 10px;
  color: #757474;
  i {
    vertical-align: text-bottom;
    font-size: 1rem;
    padding-right: 1.2rem;
    width: 0.9rem;
    margin-right: 4px;
  }
`

const CategoryText = styled('div')`
  font-size: 13px;
  color: #757474;
  font-weight: 600;
  margin-top: 7px;
  margin-left: 10px;
`

const ErrorText = styled('div')`
  font-size: 12px;
  color: #ed3c3c;
  font-weight: 400;
  margin: 7px 10px 0 10px;
`

const NeedsMoreInfoReasonsContainer = styled('div')`
  font-size: 13px;
  color: #ed3c3c;
  font-weight: 400;

  p {
    font-weight: bold;
    margin: 0;
  }

  ul,
  li {
    list-style-type: inherit;
  }

  ul {
    margin-top: 5px;
    padding-left: 20px;
  }
`

const TagItem = styled('li')`
  margin-right: 10px;
  border-bottom: 1px solid;
  color: #bababa;
  display: inline-block;
  white-space: nowrap;
  font-size: 13px;
`
const TagItems = styled('ul')`
  margin: 7px 0;
`

const EditSampleAction = styled('div')`
  padding: 0 !important;
  display: flex;
  justify-content: space-around;
  text-align: center;
  font-size: 1.35em;
`
const PlayButton = styled(Button)`
  width: 100%;
  padding: 10px 11px;
  color: #4db6ac;
`

const AuthorNameRow = styled('div')`
  color: #7f7f7f;
  font-size: 12px;
  padding-top: 7px;
  display: flex;

  @media all and (max-width: 600px) {
    font-size: 14px;
  }

  .by-author {
    padding-right: 4px;
  }
`

@inject("sampleStore")
@inject("userStore")
@observer
export default class EditSampleSmallCard extends React.Component {
  constructor(props) {
    super(props)
    this.sampleWaveform = React.createRef()
  }

  handlePlayBtnClick = (e) => {
    e.stopPropagation()
    this.sampleWaveform.current.playSoundOnce()
  }

  handleCardContentClick = (e) => {
    e.stopPropagation()
    const { sampleStore, sample } = this.props
    // Allow multi-select
    if (e.ctrlKey || e.metaKey || e.shiftKey) {
      if (!sampleStore.containsSample(sample)) {
        sampleStore.selectSample(sample)
      } else {
        sampleStore.deselectSample(sample)
      }
    } else {
      sampleStore.clearStore()
      sampleStore.selectSample(sample)
    }
  }

  renderTagChips(tagList) {
    return (
      <TagItems>
        {tagList.map((tag) => (
          <TagItem key={tag}>{_.capitalize(tag)}</TagItem>
        ))}
      </TagItems>
    )
  }

  renderNeedsMoreInfoReasons() {
    const { sample } = this.props
    const latestEvent = _.last(sample.events)
    const possibleReasons = { ...needsMoreInfoReasons, ...rejectionReasons }

    if (latestEvent && !_.isEmpty(latestEvent.reasons)) {
      return (
        <NeedsMoreInfoReasonsContainer>
          {latestEvent.state == "rejected" ? (
            <p>
              Unfortunately, we couldn't accept this sample for the following
              reasons:
            </p>
          ) : (
            <p>Please fix the following:</p>
          )}
          <ul className="browser-default">
            {latestEvent.reasons.map((reason) => {
              if (possibleReasons[reason]) {
                return <li key={reason}>{possibleReasons[reason]}</li>
              } else {
                return <li key={reason}>{reason}</li>
              }
            })}
          </ul>
        </NeedsMoreInfoReasonsContainer>
      )
    } else {
      return null
    }
  }

  renderAuthor() {
    const { sample } = this.props
    const { user_display_name_truncated, user_slug } = sample
    const userPath = "/users/" + user_slug
    return (
      <AuthorNameRow>
        <span className="by-author">By</span>
        <a href={userPath} aria-label={`Link to author`}>{user_display_name_truncated}</a>
      </AuthorNameRow>
    )
  }

  render() {
    const { sample, categories, sampleStore, userStore } = this.props

    const isSelected = sampleStore.containsSample(sample)
    const category = categories[sample.category_id]

    return (
      <EditSampleSmallCardContainer
        className="card hoverable"
        isSelected={isSelected}
      >
        <EditSampleSmallWaveformContainer
          onClick={this.handleCardContentClick}
        >
          <SampleWaveform
            sample={sample}
            duration={sample.duration}
            showSampleSelect={true}
            shouldLink={false}
            ref={this.sampleWaveform}
            isSelected={isSelected}
          />
        </EditSampleSmallWaveformContainer>
        <EditSampleSmallInfoContainer>
          {this.renderNeedsMoreInfoReasons()}
          {Boolean(
            sample.touchedFields.name && sample.validationErrors.name
          ) && <ErrorText>Add a descriptive & accurate name.</ErrorText>}
          {Boolean(sample.tempo || sample.key) && (
            <AttributesContainer>
              {sample.tempo && (
                <Attribute>
                  <i className="fas fa-ellipsis" aria-hidden="true"></i>
                  {sample.tempo}bpm
                </Attribute>
              )}
              {sample.key && (
                <Attribute>
                  <i className="fas fa-music" aria-hidden="true"></i>
                  {sample.key} {sample.mode}
                </Attribute>
              )}
            </AttributesContainer>
          )}
          {category && (
            <CategoryText>{_.capitalize(category.name)}</CategoryText>
          )}
          {Boolean(
            sample.touchedFields.category_id && sample.validationErrors.category_id
          ) && <ErrorText>Category required.</ErrorText>}
          {Boolean(
            sample.touchedFields.tag_list && sample.validationErrors.tag_list
          ) && <ErrorText>At least 4 tags required.</ErrorText>}
          {Boolean(
            sample.touchedFields.loop_or_one_shot && sample.validationErrors.loop_or_one_shot
          ) && <ErrorText>Must be tagged as a loop, a one-shot, or both.</ErrorText>}
          {Boolean(sample.touchedFields.tempo && sample.validationErrors.tempo) && <ErrorText>Tempo must be greater than 0 if present.</ErrorText>}
          {Boolean(sample.validationErrors.mode_but_not_key_present) && <ErrorText>If selecting a sample's mode, a key must also be present.</ErrorText>}
          {sample.sortedTags.length > 0 &&
            this.renderTagChips(sample.sortedTags)}
          {userStore?.currentUser?.canModerate && this.renderAuthor()}
        </EditSampleSmallInfoContainer>
        <EditSampleAction className="card-action">
          <PlayButton size="large" onClick={this.handlePlayBtnClick} aria-label="Play">
            <i className="fas fa-play" aria-hidden="true"></i>
          </PlayButton>
        </EditSampleAction>
      </EditSampleSmallCardContainer>
    )
  }
}
